import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { Rocket, Trash2, Trash2Icon } from 'lucide-react';
import { Trans } from 'react-i18next';
import { DropdownMenuItem } from '../../../components/ui/dropdown-menu.tsx';
import Text from '../../../components/ui/text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { Can } from '../../acl/can.tsx';
import type { ConfirmationDialogProps } from '../../dialog/confirmation-dialog.tsx';
import { ConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';
import { DraftOrderCreateDialog } from '../../sales-draft-new/views/create-draft-order-dialog.tsx';
import { useDetailQuoteOrder } from '../hooks/use-detail-quote-order.tsx';
import { QuoteOrderReleaseDialog } from './quote-order-release-dialog.tsx';

export type QuoteOrderDropdownMenuItemProps = {
    transactionId: string;
    type: SrSalesOrderType;
    onDelete?: () => void;
};

export const DropdownMenuItemDelete = ({
    transactionId,
    type,
    onDelete,
}: QuoteOrderDropdownMenuItemProps) => {
    const { t } = useLocale('quotes-orders-actions');
    const { toast } = useToast();
    const deleteOrderMutation = api.platoUser.command.salesOrder.order.delete.useMutation();

    const handleDelete = async () => {
        try {
            await deleteOrderMutation.mutateAsync({
                sales_order_id: transactionId,
            });

            toast({
                icon: 'success',
                title: t(`${type}__delete_toast__success_title`),
                description: (
                    <Trans
                        t={t}
                        i18nKey={`${type}__delete_toast__success_description`}
                        components={{
                            linkTag: (
                                <div
                                    className="text-blue-700 hover:underline"
                                    onClick={() => {
                                        void DialogStateManager.show(DraftOrderCreateDialog, {
                                            source: type === 'ORDER' ? 'order_detail' : 'quote_detail',
                                        });
                                    }}
                                />
                            ),
                        }}
                    />
                ),
            });

            onDelete?.();
        } catch {
            toast({
                icon: 'warning',
                title: 'Error',
            });
        }
    };

    const handleConfirmDelete = () => {
        void DialogStateManager.show<any, ConfirmationDialogProps, ConfirmationDialogProps>(
            ConfirmationDialog,
            {
                id: 'delete-confirmation-dialog',
                focus: 'destroy',
                icon: Trash2Icon,
                title: t(`${type}__delete_confirmation__title`),
                description: t(`${type}__delete_confirmation__description`),
                accept: {
                    text: t('form__delete', { ns: 'common' }),
                    action: handleDelete,
                },
                reject: {
                    text: t('form__cancel', { ns: 'common' }),
                },
            },
        );
    };

    return (
        <Can I="delete" a="QuoteOrder">
            <DropdownMenuItem onSelect={handleConfirmDelete}>
                <Trash2 className="h-4 w-4" />
                <Text>{t('dropdown_option__delete', { ns: 'quotes-orders-actions' })}</Text>
            </DropdownMenuItem>
        </Can>
    );
};

export const DropdownMenuItemRelease = ({ transactionId, type }: QuoteOrderDropdownMenuItemProps) => {
    const { t } = useLocale('quotes-orders-actions');

    const { data, isLoading } = useDetailQuoteOrder({ transactionId });

    if (isLoading || data?.header.released_date) {
        return null;
    }

    const handleSelect = () => {
        void DialogStateManager.show(QuoteOrderReleaseDialog, {
            transactionId,
            type,
        });
    };

    return (
        <Can I="release" a="QuoteOrder">
            <DropdownMenuItem onSelect={handleSelect}>
                <Rocket className="h-4 w-4" />
                <Text>{t('dropdown_option_release', { ns: 'quotes-orders-actions' })}</Text>
            </DropdownMenuItem>
        </Can>
    );
};
